import { RouteRecordRaw } from 'vue-router';
import { pageConfigMap } from 'preferenceStore-utils';
import { toRaw } from 'vue';

/**
 * 默认租户路由解析器
 * 
 * 处理标准Vue Router格式的菜单数据
 * 数据格式：{ name, path, meta, children, redirect }
 * 
 * 功能：
 * 1. 递归处理菜单树结构
 * 2. 为每个路由节点分配对应的组件（分组用PageTabs，页面用contents）
 * 3. 添加动态路由标记（_isDynamic, _isGroup, _topName等）
 * 4. 保持原始路径结构（相对路径，作为rootLayout的子路由）
 * 
 * @param data 后端返回的菜单数据数组
 * @returns Vue Router路由配置数组
 * 
 * @example
 * 输入：
 * [{
 *   name: 'p4000list',
 *   path: 'p4000list',
 *   meta: { title: '项目' },
 *   children: [{
 *     name: '4001item',
 *     path: '4001item',
 *     meta: { title: '项目列表' }
 *   }]
 * }]
 * 
 * 输出：
 * [{
 *   name: 'p4000list',
 *   path: 'p4000list',
 *   component: PageTabs,
 *   meta: { title: '项目', _isDynamic: true, _isGroup: true, ... },
 *   children: [...]
 * }]
 */
export function parseDefaultTenantMenu(data: any[]): RouteRecordRaw[] {
  if (!Array.isArray(data) || data.length === 0) return [];

  /**
   * 根据是否有子节点选择对应的组件
   * - 分组节点（hasChild=true）：使用 PageTabs，内部有 RouterView 渲染子路由
   * - 叶子节点（hasChild=false）：使用 contents，直接渲染页面内容
   * @param hasChild 是否有子节点
   * @returns 组件导入函数
   */
  const getComponent = (hasChild: boolean) =>
    hasChild
      ? () => import('@/layout/components/dynamic/PageTabs.vue')
      : () => import('@/layout/components/dynamic/contents.vue');

  /**
   * 递归处理菜单数据
   * @param children 子菜单数组
   * @param _parentPath 父路径（未使用，保留用于扩展）
   * @param topName 顶层路由名称
   * @param isTop 是否为顶层路由
   * @returns 处理后的路由配置数组
   */
  const processMenu = (children: any[] = [], _parentPath = '', topName = '', isTop = false): any[] => {

    return children.filter(child => child && typeof child === 'object').map((child: any) => {
      const hasChild = Array.isArray(child?.children) && child?.children.length > 0;
      const resolvedTopName = isTop ? child?.name : topName;

      // 构建meta信息
      const meta: any = {
        ...(child?.meta || {}),
        _isDynamic: true,        // 标记为动态路由
        _topName: resolvedTopName, // 顶层路由名称
        _isGroup: hasChild,       // 是否为分组
        ...(isTop ? { _isDynamicTop: true } : {}), // 是否为顶层动态路由

      };
      // 只有叶子节点（无子节点）才需要存储页面配置到 pageConfigMap
      // 分组节点使用 PageTabs 组件，通过 RouterView 渲染子路由，不需要 pageConfigMap
      const currentPageKey = child?.name || child?.path || '';
      if (!hasChild && currentPageKey && meta.page) {
        pageConfigMap[currentPageKey] = toRaw(meta.page);
      }
      meta.page = hasChild ? undefined : currentPageKey;

      let subChildren: any[] | undefined;
      let redirect: any = child?.redirect;

      // 递归处理子路由
      if (hasChild) {
        subChildren = processMenu(child.children, child.path, resolvedTopName, false);
      }

      return {
        ...child,
        component: getComponent(hasChild),
        meta,

        ...(redirect ? { redirect } : {}),
        ...(subChildren ? { children: subChildren } : {}),
      };
    });
  };

  return processMenu(data, '', '', true) as RouteRecordRaw[];
}
